import React, {useEffect, useState, ReactNode, useCallback} from 'react';
import {getCompanyStatistics} from '@/rest/company/get-company-statistics';
import {getAllEmployees} from '@/rest/employees/get-all-employees';
import {CompanyStatistics} from '@/types/company';
import {CompanyUser} from '@/types/user';
import CompanyContext from './context';
import {useAppSelector} from '@/redux-store/store';

export const CompanyProvider: React.FC<{
  children: ReactNode;
  companyId?: string;
}> = ({children, companyId}) => {
  const userRole = useAppSelector((state) => state.auth.keycloakUser?.userRole);
  const {isUserHealthManager} = useAppSelector((state) => state.auth);
  const isAdmin = userRole && userRole === 'ADMIN';
  const [companyStatistics, setCompanyStatistics] =
    useState<CompanyStatistics | null>(null);
  const [companyEmployees, setCompanyEmployees] = useState<
    CompanyUser[] | null
  >(null);

  const loadCompanyData = useCallback(
    async (id: string) => {
      try {
        if (isAdmin) {
          const employees = await getAllEmployees(id);
          setCompanyEmployees(employees);
        }
        if (isAdmin || isUserHealthManager) {
          const stats = await getCompanyStatistics(id);
          setCompanyStatistics(stats);
        }
      } catch (error) {
        console.error('Error fetching company data', error);
      }
    },
    [isAdmin, isUserHealthManager]
  );

  useEffect(() => {
    if (companyId) {
      loadCompanyData(companyId);
    }
  }, [companyId, loadCompanyData]);

  return (
    <CompanyContext.Provider
      value={{
        companyStatistics,
        companyEmployees,
        refreshCompanyData: () => {
          if (companyId) {
            loadCompanyData(companyId);
          }
        }
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};
